import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { es } from '@/locales/es';
import { en } from '@/locales/en';
import i18n from '@/i18n';

// ==============================================
// CONFIGURATION CONTEXT
// ==============================================
// Maneja el tema (dark/light) e idioma (es/en) de la aplicación
// Sincroniza el idioma con i18next para que Landing y toda la app
// compartan el mismo estado de idioma.
// ==============================================

const ConfigContext = createContext(undefined);

// Diccionario de traducciones
const translations = {
  es,
  en
};

export function ConfigProvider({ children }) {
  // Estado inicial desde localStorage o defaults
  const [theme, setThemeState] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved === 'light' ? 'light' : 'dark';
  });

  const [language, setLanguageState] = useState(() => {
    // Unify: check both possible localStorage keys
    const saved = localStorage.getItem('language') || localStorage.getItem('i18nextLng');
    const lang = saved === 'en' ? 'en' : 'es';
    // Sync i18next on init
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
    return lang;
  });

  // Efecto para aplicar el tema al HTML
  useEffect(() => {
    const root = document.documentElement;

    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [theme]);

  // Función para cambiar el tema
  const setTheme = useCallback((newTheme) => {
    if (newTheme === 'light' || newTheme === 'dark') {
      setThemeState(newTheme);
      localStorage.setItem('theme', newTheme);
    }
  }, []);

  // Función para cambiar el idioma — syncs BOTH systems
  const setLanguage = useCallback((newLanguage) => {
    if (newLanguage === 'es' || newLanguage === 'en') {
      setLanguageState(newLanguage);
      localStorage.setItem('language', newLanguage);
      // Sync i18next so Landing.jsx and any useTranslation() consumers also update
      if (i18n.language !== newLanguage) {
        i18n.changeLanguage(newLanguage);
      }
    }
  }, []);

  // Función helper para traducciones
  const t = useCallback((key) => {
    const keys = key.split('.');
    let value = translations[language];

    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k];
      } else {
        return key; // Retorna la clave si no encuentra la traducción
      }
    }

    return value || key;
  }, [language]);

  // Función para alternar el tema
  const toggleTheme = useCallback(() => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  }, [theme, setTheme]);

  // Función para alternar el idioma
  const toggleLanguage = useCallback(() => {
    setLanguage(language === 'es' ? 'en' : 'es');
  }, [language, setLanguage]);

  const value = {
    theme,
    language,
    setTheme,
    setLanguage,
    toggleTheme,
    toggleLanguage,
    t,
    translations: translations[language]
  };

  return (
    <ConfigContext.Provider value={value}>
      {children}
    </ConfigContext.Provider>
  );
}

// Hook para usar el contexto
export function useConfig() {
  const context = useContext(ConfigContext);

  if (context === undefined) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }

  return context;
}
